import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, Sphere } from '@react-three/drei';
import * as THREE from 'three';

function Lattice() {
  const ref = useRef<THREE.Group>(null);
  
  // Create a grid of points
  const points = useMemo(() => {
    const p = [];
    for (let x = -2; x <= 2; x += 1) {
      for (let y = -2; y <= 2; y += 1) {
        for (let z = -2; z <= 2; z += 1) {
          p.push(new THREE.Vector3(x, y, z));
        }
      }
    }
    return p;
  }, []);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.getElapsedTime() * 0.1;
      ref.current.rotation.z = state.clock.getElapsedTime() * 0.05;
    }
  });

  return (
    <group ref={ref} scale={0.8}>
      {points.map((point, i) => (
        <group key={i} position={point}>
          <Sphere args={[0.03, 16, 16]}>
            <meshBasicMaterial color="#ff5d22" transparent opacity={0.28} />
          </Sphere>
        </group>
      ))}
      {/* Subtle connections would be too heavy, let's use a simple wireframe cube instead for structure */}
      <mesh>
        <boxGeometry args={[4, 4, 4]} />
        <meshBasicMaterial color="#334155" wireframe transparent opacity={0.08} />
      </mesh>
    </group>
  );
}

function Packets() {
  const points = useMemo(() => {
    return Array.from({ length: 15 }).map(() => ({
      pos: new THREE.Vector3((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4),
      target: new THREE.Vector3((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4),
      speed: 0.1 + Math.random() * 0.2
    }));
  }, []);

  return (
    <>
      {points.map((p, i) => (
        <Packet key={i} {...p} />
      ))}
    </>
  );
}

function Packet({ pos, target, speed }: { pos: THREE.Vector3, target: THREE.Vector3, speed: number }) {
  const ref = useRef<THREE.Group>(null);
  const currentPos = useMemo(() => pos.clone(), [pos]);

  useFrame(() => {
    if (ref.current) {
      currentPos.lerp(target, speed * 0.05);
      ref.current.position.copy(currentPos);
      if (currentPos.distanceTo(target) < 0.1) {
        target.set((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4);
      }
    }
  });

  return (
    <group ref={ref}>
      <Sphere args={[0.02, 8, 8]}>
        <meshBasicMaterial color="#ff5d22" />
      </Sphere>
    </group>
  );
}

export function NetworkLattice() {
  return (
    <div className="absolute inset-0 z-0 opacity-40">
      <Canvas camera={{ position: [0, 0, 8], fov: 40 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={0.75} color="#ff5d22" />
        <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
          <Lattice />
          <Packets />
        </Float>
      </Canvas>
    </div>
  );
}
